/**
 * Routing utilities for multi-tenant customer dashboard
 *
 * Supports two routing modes:
 * 1. Development: localhost:8001/m/merchantname/route
 * 2. Production: merchantname.aiva.nz/route
 */

export interface MerchantInfo {
  id: string;
  subdomain: string;
  isDemo: boolean;
}

/**
 * Extract merchant ID from URL
 *
 * @returns Merchant ID (e.g., 'myfurbaby', 'demo')
 */
export function getMerchantFromUrl(): string {
  const hostname = window.location.hostname;
  const pathname = window.location.pathname;

  // Development mode: localhost:8001/m/merchantname
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    const match = pathname.match(/^\/m\/([^\/]+)/);
    if (match && match[1]) {
      return match[1];
    }
    return 'demo';
  }

  // Production mode: merchantname.aiva.nz
  const parts = hostname.split('.');

  // Handle different domain patterns
  if (parts.length >= 2) {
    const subdomain = parts[0];

    // Ignore www and bare domain
    if (subdomain === 'www' || subdomain === 'aiva' || parts.length === 2) {
      return 'demo';
    }

    return subdomain;
  }

  return 'demo';
}

/**
 * Get base path for routing
 * In development, this returns '/m/merchantname'
 * In production, this returns ''
 */
export function getBasePath(): string {
  const hostname = window.location.hostname;

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    const merchantId = getMerchantFromUrl();
    return `/m/${merchantId}`;
  }

  return '';
}

/**
 * Build a full path for navigation
 * Automatically handles development vs production routing
 *
 * @param path - Relative path (e.g., '/dashboard', '/pets')
 * @returns Full path including base (e.g., '/m/myfurbaby/dashboard' or '/dashboard')
 */
export function buildPath(path: string): string {
  const basePath = getBasePath();
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${basePath}${cleanPath}`;
}

/**
 * Get merchant configuration
 * In a real implementation, this would fetch from API
 * For now, returns mock data
 */
export function getMerchantInfo(merchantId: string): MerchantInfo {
  return {
    id: merchantId,
    subdomain: merchantId,
    isDemo: merchantId === 'demo',
  };
}

/**
 * Navigate to a path within the current merchant context
 * Use this instead of window.location.href for internal navigation
 */
export function navigateTo(path: string) {
  const fullPath = buildPath(path);
  window.location.href = fullPath;
}

/**
 * Check if current path matches given path
 * Useful for active link highlighting
 */
export function isActivePath(path: string, exact: boolean = false): boolean {
  const currentPath = window.location.pathname;
  const fullPath = buildPath(path);

  if (exact) {
    return currentPath === fullPath;
  }

  return currentPath.startsWith(fullPath);
}

/**
 * Get API base URL
 * Uses VITE_API_URL environment variable, with fallbacks for dev/prod
 */
export function getApiBaseUrl(): string {
  // Use environment variable if set
  const envApiUrl = import.meta.env.VITE_API_URL;
  if (envApiUrl) {
    return `${envApiUrl}/api/customer-portal`;
  }

  // Fallback based on hostname
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:7002/api/customer-portal';
  }

  return 'https://api.aiva.nz/api/customer-portal';
}

/**
 * Get current merchant ID
 * Convenience wrapper around getMerchantFromUrl
 */
export function getCurrentMerchant(): string {
  return getMerchantFromUrl();
}

/**
 * Build full URL for merchant subdomain
 * Useful for generating links to other merchants
 */
export function buildMerchantUrl(merchantId: string, path: string = '/'): string {
  const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

  if (isLocal) {
    return `http://localhost:8001/m/${merchantId}${path}`;
  }

  return `https://${merchantId}.aiva.nz${path}`;
}

/**
 * Parse query parameters from URL
 */
export function getQueryParams(): Record<string, string> {
  const params: Record<string, string> = {};
  const searchParams = new URLSearchParams(window.location.search);

  searchParams.forEach((value, key) => {
    params[key] = value;
  });

  return params;
}

/**
 * Get specific query parameter
 */
export function getQueryParam(key: string): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get(key);
}

/**
 * Check if we're in development mode
 */
export function isDevelopment(): boolean {
  return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
}

/**
 * Check if we're in production mode
 */
export function isProduction(): boolean {
  return !isDevelopment();
}
